import { resolveFrontendApiBase } from '../../utils/apiBase'
import adminAxios from '../adminAxios'

const API_BASE = resolveFrontendApiBase()

export type BenchmarkItem = {
  id?: string
  name: string
  source_text?: string
  source_text_ref?: string
  tags?: string[]
  category?: string
  difficulty?: string
  notes?: string
  glossary_terms?: string[]
  never_translate_terms?: string[]
  source_book_id?: string
  source_book_title?: string
  source_chapter_guid?: string
  source_chapter_name?: string
  word_count?: number
  genre?: string
  quotation_style?: string
  intended_audience?: string[]
  tone?: string
  french_narrative_tone?: string
  character_relationships?: string
  french_dialogue_ruleset_version?: string
  translation_metadata?: Record<string, unknown>
  translation_metadata_by_language?: Record<string, Record<string, unknown>>
}

export type BenchmarkPack = {
  id: string
  name: string
  language: string
  version: string
  description?: string
  items: BenchmarkItem[]
  item_count?: number
}

export type PipelineVariant = {
  id: string
  label?: string
  extra_translation_instruction?: string
  proofreader_enabled?: boolean
  dialogue_normalizer_enabled?: boolean
  translation_max_retries?: number
  proofreader_loops?: number
  run_final_alignment?: boolean
  proofreader_formality_model?: string
  proofreader_general_model?: string
  proofreader_alignment_model?: string
}

export type ExperimentRequest = {
  name: string
  target_language: string
  benchmark_pack_id: string
  benchmark_pack_version: string
  translation_models: string[]
  evaluator_models?: string[]
  preset: 'quick' | 'standard' | 'loop_optimization' | 'release_candidate'
  baseline_model?: string
  pipeline_variants?: PipelineVariant[]
  num_runs?: number
  apply_targets?: Record<string, unknown>
  benchmark_item_ids?: string[]
}

export type Experiment = ExperimentRequest & {
  id: string
  status: string
  total_cells: number
  queued_cells?: number
  completed_cells?: number
  failed_cells?: number
}

export type ExperimentSeriesRequest = {
  name: string
  target_language: string
  benchmark_pack_id: string
  benchmark_pack_version: string
  translation_models: string[]
  proofreader_models?: string[]
  evaluator_models?: string[]
  baseline_model?: string
  budget_cap_usd?: number
  pause_between_phases?: boolean
}

export type ExperimentSeries = ExperimentSeriesRequest & {
  id: string
  status: string
  current_phase?: string
  current_experiment_id?: string
  flow_version?: string
  phase_order?: string[]
  legacy_read_only?: boolean
  phase_results?: Record<string, unknown>
  manual_phase_promotions?: Record<string, unknown>
  pause_reason?: string
  next_phase?: string
  total_cost_usd?: number
  current_phase_cost_usd?: number
  current_phase_cost_status?: string
}

export type CandidateCostSummary = {
  key?: string
  model?: string
  variant?: string
  average_score?: number
  diagnostic_block_rate?: number
  diagnostic_blockers?: Array<{ reason: string; count: number; examples?: string[] }>
  diagnostic_warnings?: Array<{ reason: string; count: number; examples?: string[] }>
  baseline_delta?: number
  pairwise_vs_baseline?: string
  sample_count?: number
  scored_run_count?: number
  judge_score_count?: number
  avg_evaluators_per_run?: number
  run_count?: number
  completed_count?: number
  failed_count?: number
  source_word_count_total?: number
  cost_total_usd?: number
  pipeline_cost_total_usd?: number
  evaluation_cost_total_usd?: number
  avg_cost_usd?: number
  cost_per_10k_words?: number
  cost_per_10k_words_basis?: string
  missing_cost_entries?: number
  status_counts?: Record<string, number>
  status_costs?: Record<string, number>
  usage_by_stage?: Record<string, Record<string, any>>
  evaluation_mode?: string
  gate_result_count?: number
  gate_pass_rate?: number
  critical_error_count?: number
  major_error_count?: number
  minor_error_count?: number
  publication_blocker_count?: number
  major_errors_per_10k_words?: number
  minor_errors_per_10k_words?: number
  pairwise_wins?: number
  pairwise_losses?: number
  pairwise_ties?: number
  pairwise_comparisons?: number
  pairwise_score?: number
  pairwise_baseline_matchup?: Record<string, number>
  decision_penalty_rank?: number[]
  decision_quality_score?: number
  decision_quality_delta?: number
  decision_status?: string
  decision_eligible?: boolean
  hard_gate_issues?: Array<Record<string, any>>
  hard_gate_issue_summary?: Record<string, Array<Record<string, any>>>
  critical_issues?: Array<Record<string, any>>
  major_issues?: Array<Record<string, any>>
  manual_promoted?: boolean
  manual_promotion_mode?: string
}

export type ExperimentSeriesProgress = {
  series: ExperimentSeries
  status: string
  current_phase?: string
  next_phase?: string
  phases: Array<{
    phase: string
    label: string
    status: string
    result?: Record<string, any> & {
      candidate_costs?: CandidateCostSummary[]
    }
  }>
  current_experiment_progress?: ExperimentProgress | null
  total_cost_usd?: number
  current_phase_cost_usd?: number
  current_phase_cost_status?: string | null
  projected_next_phase_cells?: number
  projected_next_phase_cost_usd?: number
}

export type ExperimentProgress = {
  experiment_id: string
  status: string
  total_cells: number
  progress_percentage: number
  counts: Record<string, number>
  queue_errors?: Array<{
    message: string
    count: number
    examples?: Array<{ run_id?: string; model?: string; variant?: string; text?: string }>
  }>
  runs: Array<Record<string, unknown>>
}

export type ExperimentReport = {
  id?: string
  experiment_id: string
  language: string
  baseline_key: string
  baseline_score: number
  candidates: Array<{
    key: string
    model: string
    variant: string
    average_score: number
    sample_count: number
    scored_run_count?: number
    judge_score_count?: number
    avg_evaluators_per_run?: number
    score_min?: number
    score_max?: number
    score_stddev?: number
    category_breakdown?: Record<string, { average: number; count: number; min: number; max: number }>
    failed_count: number
    avg_latency_ms?: number
    source_word_count_total?: number
    avg_tokens?: number
    avg_input_tokens?: number
    avg_output_tokens?: number
    avg_cached_tokens?: number
    avg_reasoning_tokens?: number
    cost_total_usd?: number
    pipeline_cost_total_usd?: number
    evaluation_cost_total_usd?: number
    avg_cost_usd?: number
    cost_per_10k_words?: number
    cost_per_10k_words_basis?: string
    missing_cost_entries?: number
    usage_by_stage?: Record<string, Record<string, any>>
    evaluation_mode?: string
    gate_result_count?: number
    gate_pass_rate?: number
    critical_error_count?: number
    major_error_count?: number
    minor_error_count?: number
    publication_blocker_count?: number
    major_errors_per_10k_words?: number
    minor_errors_per_10k_words?: number
    pairwise_wins?: number
    pairwise_losses?: number
    pairwise_ties?: number
    pairwise_comparisons?: number
    pairwise_score?: number
    decision_penalty_rank?: number[]
    decision_quality_score?: number
    decision_quality_delta?: number
    decision_status?: string
    decision_eligible?: boolean
    hard_gate_issues?: Array<Record<string, any>>
    proofreader_changes?: number
    baseline_delta: number
    pairwise_vs_baseline: string
    judge_agreement?: number
    diagnostic_block_rate: number
    diagnostic_blockers?: Array<{ reason: string; count: number; examples?: string[] }>
    diagnostic_warnings?: Array<{ reason: string; count: number; examples?: string[] }>
    adjudication: string
  }>
  recommended_action: {
    action: string
    model?: string
    variant?: string
    tier?: string
    reason?: string
  }
  evaluation_mode?: string
  pairwise_summary?: Record<string, any>
  summary: Record<string, number>
}

async function parseResponse<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || fallback)
  }
  return response.json()
}

export async function listBenchmarkPacks(language?: string): Promise<BenchmarkPack[]> {
  const params = new URLSearchParams()
  if (language) params.set('language', language)
  const response = await fetch(`${API_BASE}/api/ai-testing/benchmark-packs${params.toString() ? `?${params}` : ''}`)
  return parseResponse<BenchmarkPack[]>(response, 'Failed to load benchmark packs')
}

export async function createBenchmarkPack(payload: Omit<BenchmarkPack, 'id'>): Promise<BenchmarkPack> {
  const response = await fetch(`${API_BASE}/api/ai-testing/benchmark-packs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseResponse<BenchmarkPack>(response, 'Failed to create benchmark pack')
}

export async function deleteBenchmarkPack(packId: string): Promise<Record<string, unknown>> {
  const response = await adminAxios.delete<Record<string, unknown>>(
    `${API_BASE}/api/ai-testing/benchmark-packs/${encodeURIComponent(packId)}`
  )
  return response.data
}

export type ChapterBenchmarkPackRequest = {
  name: string
  language: string
  source_user_id?: string
  version?: string
  description?: string
  min_word_count?: number
  max_chapters?: number
  random_seed?: string
  max_books_to_scan?: number
  source_book_ids?: string[]
}

export type ChapterBenchmarkSourceBook = {
  id: string
  title: string
  language?: string
  chapter_count?: number
  eligible_chapter_count?: number
  word_count_total?: number
  series?: string
  created_at?: string
  updated_at?: string
  metadata_status?: 'ready' | 'partial' | 'missing'
  metadata_score?: number
  metadata_missing?: string[]
  metadata_fields?: {
    genre?: boolean
    intended_audience?: boolean
    quotation_style?: boolean
    quotation_styles?: boolean
    quotation_styles_required_count?: number
    quotation_styles_set_count?: number
    quotation_styles_set_languages?: string[]
    quotation_styles_missing_languages?: string[]
    legacy_quotation_style?: boolean
    french_narrative_tone?: boolean
    eligible_chapters_with_relationships?: number
    eligible_chapter_relationship_coverage?: number
  }
}

export async function listChapterBenchmarkSourceBooks(params?: {
  min_word_count?: number
  max_books_to_scan?: number
}): Promise<ChapterBenchmarkSourceBook[]> {
  const response = await adminAxios.get<ChapterBenchmarkSourceBook[]>(
    `${API_BASE}/api/ai-testing/benchmark-packs/source-books`,
    { params }
  )
  return response.data
}

export async function generateChapterBenchmarkPack(payload: ChapterBenchmarkPackRequest): Promise<BenchmarkPack> {
  const response = await adminAxios.post<BenchmarkPack>(
    `${API_BASE}/api/ai-testing/benchmark-packs/from-random-chapters`,
    payload
  )
  return response.data
}

export async function createExperiment(payload: ExperimentRequest): Promise<Experiment> {
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseResponse<Experiment>(response, 'Failed to create experiment')
}

export async function listExperiments(language?: string, limit = 25): Promise<Experiment[]> {
  const params = new URLSearchParams()
  if (language) params.set('language', language)
  params.set('limit', String(limit))
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments?${params}`)
  return parseResponse<Experiment[]>(response, 'Failed to load experiments')
}

export async function createExperimentSeries(payload: ExperimentSeriesRequest): Promise<ExperimentSeries> {
  const response = await fetch(`${API_BASE}/api/ai-testing/series`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseResponse<ExperimentSeries>(response, 'Failed to create experiment series')
}

export async function listExperimentSeries(language?: string, limit = 25): Promise<ExperimentSeries[]> {
  const params = new URLSearchParams()
  if (language) params.set('language', language)
  params.set('limit', String(limit))
  const response = await fetch(`${API_BASE}/api/ai-testing/series?${params}`)
  return parseResponse<ExperimentSeries[]>(response, 'Failed to load experiment series')
}

export async function startExperimentSeries(seriesId: string): Promise<ExperimentSeries> {
  const { data } = await adminAxios.post<ExperimentSeries>(
    `${API_BASE}/api/ai-testing/series/${seriesId}/start`,
  )
  return data
}

export async function continueExperimentSeries(seriesId: string): Promise<ExperimentSeries> {
  const { data } = await adminAxios.post<ExperimentSeries>(
    `${API_BASE}/api/ai-testing/series/${seriesId}/continue`,
  )
  return data
}

export async function updateExperimentSeriesManualPromotions(
  seriesId: string,
  payload: {
    phase: string
    mode: 'add' | 'replace' | 'clear' | 'all'
    candidates?: Array<{ model?: string; variant?: string }>
  },
): Promise<ExperimentSeriesProgress> {
  const response = await fetch(`${API_BASE}/api/ai-testing/series/${seriesId}/manual-promotions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseResponse<ExperimentSeriesProgress>(response, 'Failed to update manual promotions')
}

export async function stopExperimentSeries(seriesId: string): Promise<ExperimentSeries> {
  const response = await fetch(`${API_BASE}/api/ai-testing/series/${seriesId}/stop`, { method: 'POST' })
  return parseResponse<ExperimentSeries>(response, 'Failed to stop experiment series')
}

export async function deleteExperimentSeries(seriesId: string): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/api/ai-testing/series/${seriesId}`, { method: 'DELETE' })
  return parseResponse<Record<string, unknown>>(response, 'Failed to delete experiment series')
}

export async function getExperimentSeriesProgress(seriesId: string): Promise<ExperimentSeriesProgress> {
  const response = await fetch(`${API_BASE}/api/ai-testing/series/${seriesId}/progress`)
  return parseResponse<ExperimentSeriesProgress>(response, 'Failed to load experiment series progress')
}

export async function exportExperimentSeriesReport(seriesId: string, detail: 'summary' | 'detailed'): Promise<Record<string, unknown>> {
  const params = new URLSearchParams({ detail })
  const response = await fetch(`${API_BASE}/api/ai-testing/series/${seriesId}/report?${params}`)
  return parseResponse<Record<string, unknown>>(response, 'Failed to export experiment series report')
}

export async function runExperiment(experimentId: string): Promise<Record<string, unknown>> {
  const { data } = await adminAxios.post<Record<string, unknown>>(
    `${API_BASE}/api/ai-testing/experiments/${experimentId}/run`,
  )
  return data
}

export async function cancelExperiment(experimentId: string): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments/${experimentId}/cancel`, { method: 'POST' })
  return parseResponse<Record<string, unknown>>(response, 'Failed to stop experiment')
}

export async function getExperimentProgress(experimentId: string): Promise<ExperimentProgress> {
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments/${experimentId}/progress`)
  return parseResponse<ExperimentProgress>(response, 'Failed to load experiment progress')
}

export async function getExperimentReport(experimentId: string): Promise<ExperimentReport> {
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments/${experimentId}/report`)
  return parseResponse<ExperimentReport>(response, 'Failed to load experiment report')
}

export async function exportExperimentReport(experimentId: string, detail: 'summary' | 'detailed'): Promise<Record<string, unknown>> {
  const params = new URLSearchParams({ detail })
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments/${experimentId}/export?${params}`)
  return parseResponse<Record<string, unknown>>(response, 'Failed to export experiment report')
}

export async function applyExperimentRecommendation(
  experimentId: string,
  payload: { confirm: boolean; language?: string; updates?: Array<{ modelId: string; tier: string }> },
): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/api/ai-testing/experiments/${experimentId}/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseResponse<Record<string, unknown>>(response, 'Failed to apply experiment recommendation')
}
